import { useMemo, useState } from 'react'
import {Map as MapIcon, Download, Copy, RefreshCw, ExternalLink, CheckCircle2} from 'lucide-react'
import toast from 'react-hot-toast'
import { useSeoPages } from '@/hooks/useSeoPages'
import { buildSitemapEntries, renderSitemapXml, SITE_BASE_URL } from '@/lib/sitemap'

/**
 * Admin tool to (re)generate `sitemap.xml` automatically from project routes
 * + active SEO pages (`isActive !== false`).
 *
 * Workflow :
 *   1. Load merged SEO pages (statiques + overrides Lumi `seo_pages`).
 *   2. Build a deduplicated list of canonical URLs (filtering inactive pages
 *      and dynamic param routes).
 *   3. Render Google-compliant XML.
 *   4. Allow the admin to copy the XML or download `sitemap.xml`, to be uploaded
 *      into `/public/sitemap.xml` for the next deployment.
 */
export default function SitemapEditor() {
  const { mergedPages, loading, error } = useSeoPages()
  const [copied, setCopied] = useState(false)

  const entries = useMemo(() => buildSitemapEntries(mergedPages), [mergedPages])
  const xml = useMemo(() => renderSitemapXml(entries), [entries])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(xml)
      setCopied(true)
      toast.success('XML copié dans le presse-papiers.')
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      console.error('[sitemap] copy failed', e)
      toast.error('Impossible de copier — copiez manuellement depuis l\u2019aperçu.')
    }
  }

  const handleDownload = () => {
    try {
      const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'sitemap.xml'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('sitemap.xml téléchargé.')
    } catch (e) {
      console.error('[sitemap] download failed', e)
      toast.error('Échec du téléchargement.')
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-900 text-white flex items-center justify-center flex-shrink-0">
            <MapIcon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-gray-900">Génération automatique du sitemap.xml</h2>
            <p className="text-sm text-gray-600 mt-1">
              Ce sitemap est construit automatiquement à partir des <strong>routes canoniques</strong> du
              site et des <strong>pages SEO actives</strong> (les pages SEO désactivées dans l\u2019admin
              sont exclues, les redirections 301 et les routes privées également).
            </p>
            <ul className="mt-3 text-xs text-gray-600 space-y-1 list-disc pl-5">
              <li>
                URL publique attendue&nbsp;: <code className="font-mono text-gray-800">{SITE_BASE_URL}/sitemap.xml</code>
              </li>
              <li>
                Téléchargez le fichier ci-dessous puis remplacez{' '}
                <code className="font-mono text-gray-800">public/sitemap.xml</code> dans le projet.
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 flex items-center gap-3">
          {loading ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              Chargement des pages SEO…
            </span>
          ) : error ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              Erreur de chargement&nbsp;: {String(error)}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {entries.length} URL{entries.length > 1 ? 's' : ''} canonique{entries.length > 1 ? 's' : ''} prêtes
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href="/sitemap.xml"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-gray-200 text-gray-700 hover:bg-gray-100"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Voir le sitemap actuel
          </a>
          <button
            onClick={handleCopy}
            disabled={loading || !!error}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-gray-200 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Copy className="w-3.5 h-3.5" />
            {copied ? 'Copié !' : 'Copier le XML'}
          </button>
          <button
            onClick={handleDownload}
            disabled={loading || !!error}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-3.5 h-3.5" />
            Télécharger sitemap.xml
          </button>
        </div>
      </div>

      {/* URL list (compact) */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">URLs incluses ({entries.length})</h3>
          <span className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">
            Triées par priorité
          </span>
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
          {entries.map((e) => (
            <div
              key={e.path}
              className="flex items-center justify-between gap-3 px-4 py-2 text-xs hover:bg-gray-50"
            >
              <a
                href={e.path}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-gray-800 truncate hover:underline"
                title={`${SITE_BASE_URL}${e.path}`}
              >
                {e.path}
              </a>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono">
                  {e.changefreq ?? 'monthly'}
                </span>
                <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 font-mono">
                  p={e.priority?.toFixed(2) ?? '0.50'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* XML Preview */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">Aperçu XML</h3>
          <span className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">
            Prêt pour /public/sitemap.xml
          </span>
        </div>
        <pre className="p-4 text-[11px] font-mono text-gray-800 bg-gray-50 overflow-x-auto max-h-96 leading-relaxed">
          {xml}
        </pre>
      </div>
    </div>
  )
}
